import { Box, Button, Paper, Skeleton, Typography, useTheme } from "@mui/material";
import DataGridCustomKeywordsScrap from "src/components/data-grid/data-grid-custom-keywords-scraping";
import Iconify from "src/components/iconify";
import { useBoolean } from "src/hooks/use-boolean";
import { useState, useCallback, useEffect, lazy } from "react";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useAddKeywordScrapingMutation, useDeleteScrapingMutation, useGetScrapingQuery, useLazyTriggerBOQuery, useScrapingExistMutation, useUpdateScrapingMutation } from "src/context/api/admin/api";
import { enqueueSnackbar } from "notistack";
import { GridRowModel } from "@mui/x-data-grid";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation } from "react-router";
import { schema, defaultValues } from "./types";
import { useScrapping } from "./add-dialog";


const ScrappingDialog = lazy(() => import('./add-dialog'));

export default function AdminScrapping() {
    const theme = useTheme();
    const workspace: any = useCurrentWorkspace();
    const [addKeywordScraping] = useAddKeywordScrapingMutation();
    const [updateScraping] = useUpdateScrapingMutation();
    const [deleteScraping] = useDeleteScrapingMutation();
    const [isEdited, setIsEdited] = useState(false);
    const [editedRows, setEditedRows] = useState<{ id: string | number, changes: Partial<GridRowModel> }[]>([]);
    const [isReset, setIsReset] = useState(false);
    const location = useLocation();
    const [scrapingExist] = useScrapingExistMutation();
    const [triggerBO] = useLazyTriggerBOQuery();

    const { data: dataScraping, error: dataScrapingError, isFetching: isFetchingDataScraping, refetch } = useGetScrapingQuery({
        workspaceId: workspace?.id,
    });

    const dataScrap = dataScraping?.map((item: any) => ({
        id: item?.id,
        keyword: item?.keyword,
        country: item?.country,
        frequency: item?.frequency,
        device: item?.device,
        updated_at: item?.updated_at,
        platform_scraping: item?.platform_scraping,
        status: item?.status,
    }));

    
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const { handleSubmit, reset } = methods;
    
    const handleAddKeywords = async (data: any) => {
        try {
            const countries = Array.isArray(data.selectedCountry) ? data.selectedCountry : [];
            if (countries.length === 0) {
                enqueueSnackbar("Please select at least one country.", { variant: "warning" });
                return;
            }
            
            const keywordsData = countries.flatMap((country: any) =>
                data.keywords.map((keyword: any) => ({
                    keyword,
                    country: [country?.country_name],
                    country_code: [country?.country_code],
                    home_language: country?.country_language,
                    frequency: data.frequency,
                    platform_scraping: data?.platform_scraping,
                    device: data.device,
                    workspaceId: workspace?.id,
                    client: workspace?.bqTable,
                    mode: "Scraping"
                }))
            );
            
            if (keywordsData.length === 0) {
                enqueueSnackbar("No keywords or countries provided.", { variant: "warning" });
                return;
            }
            
            // Check for existing keywords
            const response: any = await scrapingExist({
                keywords: keywordsData.map((k: any) => k.keyword),
                country: countries.map((c: any) => c.country_name),
                platform_scraping: data?.platform_scraping,
                workspaceId: workspace?.id,
            }).unwrap();
            
            if (response.exists) {
                enqueueSnackbar("Some keywords already exist!", { variant: "warning" });
                return;
            }
            
            // Add keywords
            await Promise.all(keywordsData.map((keywordData: any) => addKeywordScraping(keywordData).unwrap()));
            
            triggerBO();
            enqueueSnackbar("Keywords added successfully!", { variant: "success" });
            reset();
            setKeywordCount([]);
            scrapping.onClose();
            refetch();
        } catch (error: any) {
            setTitleScrapExist(error?.data?.title || "Error");
            setDescriptionScrapExist(error?.data?.description || "Failed to process request.");
            enqueueSnackbar("Failed to add keywords", { variant: "error" });
        }
    };

    const scrapping = useScrapping(handleAddKeywords);
    const {setTitleScrapExist, setDescriptionScrapExist, setKeywordCount} = scrapping;
    const handleSave = useCallback(async () => {
        try {
            const updates: any = editedRows.map(({ id, changes }) => {
                const { ...otherChanges } = changes;

                const updateData = {
                    id,
                    ...otherChanges,
                };

                return updateData;
            });

            const duplicateIds = updates
                .filter((item: any) => item.isDuplicate === true)
                .map((item: any) => item.id);

            const deletedIds: any = updates
                .filter((item: any) => item.isDeleted === true)
                .map((item: any) => item.id);

            if (duplicateIds.length > 0) {
                await Promise.all(
                    duplicateIds.map((id: any) => deleteScraping({ scraping_id: id }).unwrap())
                );
                enqueueSnackbar("Duplicates removed successfully", { variant: 'success' });
            }

            if (deletedIds.length > 0) {
                await Promise.all(
                    deletedIds.map((id: any) => deleteScraping({ scraping_id: id }).unwrap())
                );
                enqueueSnackbar("Items removed successfully", { variant: 'success' });
            }

            if (updates.length > 0) {
                await updateScraping({ updates }).unwrap();
                enqueueSnackbar("Edited successfully", { variant: 'success' });
            }

            triggerBO();
            refetch();
            setIsEdited(false);
            setEditedRows([]);
        } catch (error) {
            enqueueSnackbar("Failed to edit", { variant: 'error' });
        }
    }, [editedRows, updateScraping, deleteScraping, refetch]);

    const handleEditedRowsChange = useCallback((newEditedRows: any) => {
        setEditedRows(newEditedRows);
        setIsEdited(newEditedRows.length > 0);
    }, []);

    const handleDiscard = () => {
        setIsReset(true);
        setEditedRows([]);
        setIsEdited(false);
        setTimeout(() => setIsReset(false), 0);
    };

    useEffect(() => {
        if (location?.pathname?.split("/")[4] === "add") {
            scrapping.onOpen();
        }
    }, [location]);

    return (
        <Box px={"24px"}>
            <Paper
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
                    boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                    height: 'auto',
                    borderRadius: 2,
                    marginBottom: 1,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginBottom: 35 }}>
                    <div style={{ paddingTop: 18, paddingLeft: 25, paddingRight: 25, marginBottom: '5px' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Live Branded Keywords Monitoring
                        </Typography>

                        <Typography variant="overline" color="textSecondary" sx={{ textTransform: 'none', fontSize: 14 }}>
                            List of branded keyword SERPs monitored in real time by Primelis Signal live scraping feature
                        </Typography>
                    </div>
                    <div style={{ paddingTop: 18, paddingLeft: 25, paddingRight: 25 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => scrapping.onOpen()}
                            disabled={editedRows?.length !== 0}
                            startIcon={<Iconify icon="mingcute:add-line" />}
                        >
                            Add keyword(s)
                        </Button>

                        {editedRows?.length !== 0 && (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleDiscard}
                                    startIcon={<Iconify icon="material-symbols:refresh" />}
                                    sx={{ ml: 1, backgroundColor: '#232b35', color: 'white' }}
                                >
                                    Discard All Changes
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleSave}
                                    sx={{ ml: 1, backgroundColor: "#4b1db0", color: 'white' }}
                                    startIcon={<Iconify icon="tabler:check" />}
                                >
                                    Apply All Changes
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {isFetchingDataScraping || (!dataScraping && !dataScrapingError) ? (
                    <Skeleton
                        width={'100%'}
                        height={'480px'}
                        sx={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}
                        variant="rectangular"
                        animation={"wave"}
                    />
                ) : (
                    <DataGridCustomKeywordsScrap
                        pageSize={20}
                        onEdit={(it: any) => setIsEdited(it)}
                        handleSave={handleEditedRowsChange}
                        data={dataScrap}
                        onApply={isEdited}
                        onReset={isReset}
                    />
                )}
                <ScrappingDialog
                    {...scrapping}
                    methods={methods}
                />
            </Paper>
        </Box>
    );
}
